import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { requestPaymentApi } from "../../lib/api";

const initialValues = { payer_name: "", email: "", amount: "" };

export const PaymentPage = () => {
  const { token } = useParams();
  const [payee, setPayee] = useState(null);
  const [pageState, setPageState] = useState("loading");
  const [values, setValues] = useState(initialValues);
  const [fieldErrors, setFieldErrors] = useState({});
  const [requestError, setRequestError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setPageState("not-found");
      return;
    }

    const controller = new AbortController();

    const loadPayee = async () => {
      try {
        const { response, data } = await requestPaymentApi(
          `/api/pay/${encodeURIComponent(token)}`,
          {
            signal: controller.signal,
          },
        );

        if (response.ok && data.status === "success") {
          setPayee(data);
          setPageState("ready");
          return;
        }

        if (response.status === 404) {
          setPageState("not-found");
          return;
        }

        setRequestError(data.message || "We could not load this payment link.");
        setPageState("error");
      } catch (error) {
        if (error.name !== "AbortError") {
          setRequestError("Check your connection and try again.");
          setPageState("error");
        }
      }
    };

    loadPayee();

    return () => controller.abort();
  }, [token]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setFieldErrors((current) => ({ ...current, [name]: "" }));
    setRequestError("");
  };

  const validateForm = () => {
    const amount = Number(values.amount);
    const errors = {};

    if (!values.payer_name.trim()) {
      errors.payer_name = "Enter your full name.";
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      errors.email = "Enter a valid email address.";
    }

    if (!Number.isFinite(amount) || amount < 100) {
      errors.amount = "The minimum payment is ₦100.";
    }

    setFieldErrors(errors);
    return { isValid: Object.keys(errors).length === 0, amount };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setRequestError("");

    const { isValid, amount } = validateForm();
    if (!isValid) return;

    setIsSubmitting(true);

    try {
      const { response, data } = await requestPaymentApi(
        `/api/pay/${encodeURIComponent(token)}/initiate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            payer_name: values.payer_name.trim(),
            email: values.email.trim(),
            amount,
          }),
        },
      );

      if (!response.ok || data.status !== "success" || !data.authorization_url) {
        setRequestError(data.message || "We could not start this payment. Please try again.");
        setIsSubmitting(false);
        return;
      }

      window.location.assign(data.authorization_url);
    } catch {
      setRequestError("Check your connection and try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-grey-50 satoshi text-grey-900">
      <header className="flex h-16 items-center justify-between border-b border-grey-200 bg-white px-4 sm:h-[76px] sm:px-8 lg:px-[72px]">
        <a href="/" aria-label="MyCardGlobal home">
          <img
            className="h-auto max-h-[42px] w-24 object-contain sm:w-28"
            src="/logo.svg"
            alt="MyCardGlobal"
          />
        </a>
        <span className="inline-flex items-center gap-2 text-xs font-medium text-grey-600 sm:text-[13px]">
          <span
            className="h-2 w-2 rounded-full bg-green shadow-[0_0_0_3px_rgba(65,147,94,0.12)]"
            aria-hidden="true"
          />
          Secure payment
        </span>
      </header>

      <main className="mx-auto w-[calc(100%-2rem)] max-w-[500px] py-9 sm:py-14">
        <div className="mb-6 text-center">
          <p className="mb-2 text-[11px] font-bold tracking-[0.08em] text-primary">
            PERSONAL PAYMENT LINK
          </p>
          <h1 className="text-[30px] font-bold leading-[34px] tracking-[-0.02em] text-deep-primary sm:text-[36px] sm:leading-[40px]">
            Make a payment
          </h1>
          <p className="mt-2 text-sm text-grey-600 sm:text-base">
            Send money securely to a MyCardGlobal user.
          </p>
        </div>

        <section
          className="rounded-2xl border border-grey-200 bg-white p-5 shadow-sm sm:p-7"
          aria-busy={pageState === "loading"}
        >
          {pageState === "loading" && (
            <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 text-center text-grey-600">
              <span
                className="h-6 w-6 animate-spin rounded-full border-2 border-grey-200 border-t-primary motion-reduce:animate-none"
                aria-hidden="true"
              />
              <p className="text-sm">Loading payment details...</p>
            </div>
          )}

          {pageState === "not-found" && (
            <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 text-center text-grey-600">
              <h2 className="text-2xl font-bold text-deep-primary">Link not found</h2>
              <p className="max-w-[320px] text-sm">
                This payment link does not exist or has expired.
              </p>
            </div>
          )}

          {pageState === "error" && (
            <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 text-center text-grey-600">
              <h2 className="text-2xl font-bold text-deep-primary">Payment link unavailable</h2>
              <p className="max-w-[340px] text-sm">{requestError}</p>
              <button
                className="soft-shrink mt-2 rounded-[8px] border border-primary bg-primary px-4 py-2 text-xs font-bold text-white md:text-sm"
                type="button"
                onClick={() => window.location.reload()}
              >
                Try again
              </button>
            </div>
          )}

          {pageState === "ready" && payee && (
            <>
              <div className="flex items-center gap-3 border-b border-grey-200 pb-5">
                {payee.avatar_url ? (
                  <img
                    src={payee.avatar_url}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-primary text-lg font-bold text-white">
                    {payee.name?.trim()?.charAt(0)?.toUpperCase() || "M"}
                  </span>
                )}

                <div className="min-w-0">
                  <p className="text-xs text-grey-500">You are paying</p>
                  <p className="break-words text-base font-semibold text-deep-primary">
                    {payee.name}
                  </p>
                </div>
              </div>

              <form className="mt-5 flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-grey-900">Your full name</span>
                  <input
                    id="payer_name"
                    name="payer_name"
                    autoComplete="name"
                    placeholder="e.g. Tunde Adeyemi"
                    value={values.payer_name}
                    onChange={updateField}
                    aria-invalid={Boolean(fieldErrors.payer_name)}
                    aria-describedby={fieldErrors.payer_name ? "payer-name-error" : undefined}
                    className="w-full rounded-lg border border-grey-300 px-4 py-3 text-sm text-grey-900 placeholder:text-grey-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  {fieldErrors.payer_name && (
                    <small id="payer-name-error" className="text-xs text-pink">
                      {fieldErrors.payer_name}
                    </small>
                  )}
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-grey-900">Your email</span>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={values.email}
                    onChange={updateField}
                    aria-invalid={Boolean(fieldErrors.email)}
                    aria-describedby={fieldErrors.email ? "email-error" : undefined}
                    className="w-full rounded-lg border border-grey-300 px-4 py-3 text-sm text-grey-900 placeholder:text-grey-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  {fieldErrors.email && (
                    <small id="email-error" className="text-xs text-pink">
                      {fieldErrors.email}
                    </small>
                  )}
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-grey-900">Amount</span>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-grey-600">
                      ₦
                    </span>
                    <input
                      id="amount"
                      name="amount"
                      type="number"
                      min="100"
                      step="0.01"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={values.amount}
                      onChange={updateField}
                      aria-invalid={Boolean(fieldErrors.amount)}
                      aria-describedby={fieldErrors.amount ? "amount-error" : "amount-hint"}
                      className="w-full rounded-lg border border-grey-300 py-3 pl-8 pr-4 text-sm text-grey-900 placeholder:text-grey-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <small
                    id={fieldErrors.amount ? "amount-error" : "amount-hint"}
                    className={`text-xs ${fieldErrors.amount ? "text-pink" : "text-grey-500"}`}
                  >
                    {fieldErrors.amount || "Minimum payment is ₦100."}
                  </small>
                </label>

                {requestError && <p className="text-sm text-pink">{requestError}</p>}

                <button
                  className="soft-shrink rounded-[8px] border border-primary bg-primary px-4 py-3 text-xs font-bold text-white md:text-sm disabled:cursor-not-allowed disabled:opacity-80"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Connecting to Paystack..." : "Continue to payment"}
                </button>
              </form>

              <p className="mt-4 text-center text-xs text-grey-500">
                Payments are securely processed by Paystack.
              </p>
            </>
          )}
        </section>

        <p className="mt-4 text-center text-xs text-grey-500">
          Your payment goes directly to {payee?.name || "the recipient"}.
        </p>
      </main>
    </div>
  );
};
